/**
 * The sale being rung up, kept on the till as it is built.
 *
 * A basket used to live only in memory, so a power cut or a closed app lost
 * whatever the cashier had scanned — at the worst moment, with the customer
 * still standing there. It is small and changes on every tap, so it goes to
 * localStorage, which writes synchronously and survives a restart; one key per
 * cashier, so the next person to sign in does not inherit somebody else's
 * half-finished sale.
 *
 * Its sale id and idempotency key are kept with it. That matters in one case:
 * the power dies after the sale was completed but before the basket was
 * cleared. `wasCompleted` catches it — the sale is already on the till — so the
 * basket is dropped rather than offered for payment a second time.
 */

import { db } from "@/db/database";

export interface SavedBasket<Line> {
  lines: Line[];
  saleId: string;
  idempotencyKey: string;
}

const PREFIX = "smartspaza.basket.";

export const basketRepository = {
  load<Line>(cashierId: string): SavedBasket<Line> | null {
    try {
      const raw = globalThis.localStorage?.getItem(PREFIX + cashierId);
      if (!raw) return null;
      const saved = JSON.parse(raw) as SavedBasket<Line>;
      return Array.isArray(saved.lines) && saved.saleId && saved.idempotencyKey ? saved : null;
    } catch {
      // Unreadable or blocked storage: start an empty basket, which is what
      // happened before this existed.
      return null;
    }
  },

  save<Line>(cashierId: string, basket: SavedBasket<Line>): void {
    try {
      if (basket.lines.length === 0) {
        globalThis.localStorage?.removeItem(PREFIX + cashierId);
      } else {
        globalThis.localStorage?.setItem(PREFIX + cashierId, JSON.stringify(basket));
      }
    } catch {
      // Full or blocked storage. The basket still works; it just will not
      // survive a restart.
    }
  },

  /** Whether a sale with this id was already completed on this till. */
  async wasCompleted(saleId: string): Promise<boolean> {
    try {
      return (await db.pendingSales.get(saleId)) != null;
    } catch {
      return false;
    }
  },
};
